import { useCallback, useState } from 'react'
import { useMeetingManager } from 'modules/amazon-chime-sdk-component-library-devextreme'
import {
  useJoinMeetingMutation,
  useStartMeetingMutation,
} from '@crew/apis/meeting/meetingApis'
import { useAppDispatch } from 'states/hooks'
import { MeetingSessionConfiguration } from 'amazon-chime-sdk-js'
import { MeetingManagerJoinOptions } from 'modules/amazon-chime-sdk-component-library-devextreme/providers/MeetingProvider/types'
import {
  setIsEndMeeting,
  setJoinState,
} from 'features/webMeeting/states/webMeetingSlice'
import { JoinMeetingType } from '@crew/enums/app'
import { showMeetingPanelUpdated } from 'features/event/components/eventDetailPage/states/eventDetailPageSlice'

export const useWebMeetingJoinForm = () => {
  const dispatch = useAppDispatch()

  const meetingManager = useMeetingManager()

  const [joinMeetingMutation] = useJoinMeetingMutation()
  const [startMeetingMutation] = useStartMeetingMutation()

  // 会議の主催者か
  const [isHost, setIsHost] = useState<boolean>(false)

  // join meeting
  const joinMeeting = useCallback(
    async (displayName: string, eventId: string) => {
      const joinMeetingResponse = await joinMeetingMutation({
        displayName,
        joinMeetingType: JoinMeetingType.Event,
        eventId,
        callId: null,
      }).unwrap()

      // save joinState to store
      dispatch(setJoinState(joinMeetingResponse.joinInfo.joinState))

      // TODO: このMeetingSessionConfiguration生成の目的(joinに与えることで何を実現しようとしているか)をコメント追記する
      // https://break-tmc.atlassian.net/browse/CREW-19926
      const meetingSessionConfiguration = new MeetingSessionConfiguration(
        joinMeetingResponse.joinInfo.meeting,
        joinMeetingResponse.joinInfo.attendee
      )

      // VoiceFocusを使用可能にするためにミーティング接続時、オプションを指定
      const joinOptions: MeetingManagerJoinOptions = {
        enableWebAudio: true,
      }

      // Create a `MeetingSession` using `join()` function with the `MeetingSessionConfiguration`
      await meetingManager.join(meetingSessionConfiguration, joinOptions)

      // 会議の主催者かどうかを設定
      setIsHost(joinMeetingResponse.joinInfo.isHost)
    },
    [dispatch, joinMeetingMutation, meetingManager]
  )

  // start meeting
  const startMeeting = useCallback(
    async (eventId: string) => {
      // meetingIdが存在しない場合は正しく処理できないため処理を中断する
      if (!meetingManager.meetingId) {
        return
      }

      // 会議開始APIへのリクエストを実行
      await startMeetingMutation({
        meetingId: meetingManager.meetingId,
        eventId: eventId,
      }).unwrap()

      // At this point you could let users setup their devices, or by default
      // the SDK will select the first device in the list for the kind indicated
      // by `deviceLabels` (the default value is DeviceLabels.AudioAndVideo)

      // Start the `MeetingSession` to join the meeting
      await meetingManager.start()

      // Web会議コンポーネントに切り替え
      dispatch(showMeetingPanelUpdated(true))
      // 会議の退室判定を初期化
      dispatch(setIsEndMeeting(false))
    },
    [dispatch, meetingManager, startMeetingMutation]
  )

  return {
    joinMeeting,
    startMeeting,
    isHost,
  }
}
